import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { useCache } from '../context/CacheContext';
import { teacherApiService, StaffApiResponse } from '../services/teacherApiService';
import './DisciplinesSectionStyle.css';
import { CacheWarning } from '../th-components/CacheWarning';

export interface Discipline {
  idTeacher: number;
  idSubject: number;
  subjectName: string;
  course: number;
  countGroup: number;
}

interface Props {
  onDisciplineSelect?: (disciplineName: string | undefined) => void;
  selectedDiscipline?: string;
}

export const DisciplinesSection: React.FC<Props> = ({ onDisciplineSelect, selectedDiscipline }) => {
  const { user } = useUser();
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isUsingCache, showCacheWarning, setShowCacheWarning, forceCacheCheck } = useCache();

  const [activeTab, setActiveTab] = useState<'semesters' | 'course'>('semesters');
  const [selectedSemester, setSelectedSemester] = useState<'first' | 'second'>('first');
  const [selectedCourse, setSelectedCourse] = useState<'all' | '1' | '2' | '3' | '4'>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Функция для проверки наличия кэшированных данных дисциплин
  const checkCachedDisciplinesData = useCallback((): boolean => {
    try {
      // Проверяем различные ключи кэша, которые могут содержать данные дисциплин
      const teacherId = localStorage.getItem('teacher_id');
      if (teacherId) {
        const cacheKeys = [
          `teacher_disciplines_full_${teacherId}`,
          `teacher_disciplines_${teacherId}`,
          'all_staff'
        ];
        
        for (const key of cacheKeys) {
          const cached = localStorage.getItem(`cache_${key}`);
          if (cached) {
            return true;
          }
        }
      }
      
      // Ищем любые кэшированные данные дисциплин в localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('cache_teacher_disciplines_')) {
          return true;
        }
      }
      
      return false;
    } catch (err) {
      console.error('Error checking disciplines cache:', err);
      return false;
    }
  }, []);

  const fetchDisciplines = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      setShowCacheWarning(false);

      if (forceRefresh) {
        setRefreshing(true);
      }

      console.log('Загрузка дисциплин преподавателя...');
      
      // ПРОВЕРКА С ОТЧЕСТВОМ И БЕЗ
      if (!user?.name || !user?.lastName) {
        throw new Error('Недостаточно данных пользователя для поиска: требуется имя и фамилия');
      }

      // Инвалидируем кэш при принудительном обновлении
      if (forceRefresh) {
        let teacher: StaffApiResponse | null = null;

        // ПЕРВАЯ ПОПЫТКА: поиск с отчеством (если оно есть)
        if (user.patronymic && user.patronymic.trim() !== '') {
          teacher = await teacherApiService.findTeacherByName(
            user.name, 
            user.lastName, 
            user.patronymic
          );
        }

        // ВТОРАЯ ПОПЫТКА: если не нашли с отчеством, ищем только по имени и фамилии
        if (!teacher) {
          teacher = await teacherApiService.findTeacherByNameWithoutPatronymic(
            user.name, 
            user.lastName
          );
        }

        if (teacher) {
          teacherApiService.invalidateTeacherCache(teacher.id);
        }
      }

      let teacher: StaffApiResponse | null = null;

      // ПЕРВАЯ ПОПЫТКА: поиск с отчеством (если оно есть)
      if (user.patronymic && user.patronymic.trim() !== '') {
        console.log('Поиск преподавателя с отчеством:', user.name, user.lastName, user.patronymic);
        teacher = await teacherApiService.findTeacherByName(
          user.name, 
          user.lastName, 
          user.patronymic
        );
      }

      // ВТОРАЯ ПОПЫТКА: если не нашли с отчеством, ищем только по имени и фамилии
      if (!teacher) {
        console.log('Преподаватель не найден с отчеством, поиск только по имени и фамилии:', user.name, user.lastName);
        teacher = await teacherApiService.findTeacherByNameWithoutPatronymic(
          user.name, 
          user.lastName
        );
      }

      if (!teacher) {
        throw new Error('Преподаватель не найден');
      }

      // Получаем дисциплины - cacheService автоматически обработает кэширование
      const teacherDisciplines = await teacherApiService.getTeacherDisciplinesFull(teacher.id);
      
      setDisciplines(teacherDisciplines);
      console.log('Дисциплины преподавателя загружены:', teacherDisciplines);
      
    } catch (err: any) {
      console.error('Ошибка при загрузке дисциплин:', err);
      
      // Проверяем, является ли ошибка сетевой
      const isNetworkError = 
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError') ||
        err.message?.includes('Network request failed') ||
        err.message?.includes('Превышено время ожидания') ||
        err.name === 'TypeError';
      
      if (isNetworkError) {
        // Проверяем, есть ли кэшированные данные
        const hasCachedData = checkCachedDisciplinesData();
        
        if (hasCachedData) {
          setShowCacheWarning(true);
          
          // Пытаемся загрузить данные из кэша
          try {
            const teacherId = localStorage.getItem('teacher_id');
            if (teacherId) {
              const cachedDisciplines = localStorage.getItem(`cache_teacher_disciplines_full_${teacherId}`);
              if (cachedDisciplines) {
                const parsedDisciplines = JSON.parse(cachedDisciplines);
                setDisciplines(parsedDisciplines);
              }
            }
          } catch (cacheError) {
            console.error('Error loading cached disciplines:', cacheError);
          }
        } else {
          setError('Отсутствует подключение к интернету и нет кэшированных данных дисциплин.');
        }
      } else {
        setError('Не удалось загрузить данные дисциплин');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Функция принудительного обновления данных
  const handleRefresh = async () => {
    await fetchDisciplines(true);
  };

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    if (user) {
      fetchDisciplines();
    }
  }, [user]);

  // Компонент информационной иконки
  const InfoIcon = () => (
    <div className="info-icon-btn" tabIndex={0}>
      <button className="header-btn" type="button">
        <span className="info-icon-text">i</span>
        <span>Информация</span>
      </button>
      <div className="info-tooltip small">
        <div className="info-tooltip-content">
          <div className="info-header">
            <div className="info-title">
              <h3>Управление дисциплинами</h3>
              <p>Здесь отображаются все преподаваемые дисциплины. Вы можете фильтровать их по семестрам или курсам, а также сортировать по названию.</p>
            </div>
          </div>
          
          <div className="info-section">
            <h4>Основные возможности</h4>
            <div className="features-grid">
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Просмотр всех преподаваемых дисциплин</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Фильтрация по семестрам и курсам</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Сортировка по названию дисциплины</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Быстрый переход к группам по дисциплине</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon"></span>
                <span>Просмотр курса и количества групп</span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h4>Как использовать</h4>
            <div className="usage-steps">
              <div className="steps">
                <span className="steps-number">1</span>
                <span>Например, выберите курс для фильтрации дисциплин</span>
              </div>
              <div className="steps">
                <span className="steps-number">2</span>
                <span>Нажмите на карточку дисциплины для перехода к группам</span>
              </div>
            </div>
          </div>
          <div className="info-tip">
            Для удобства используйте фильтрацию при большом количестве дисциплин
          </div>
        </div>
      </div>
    </div>
  );

  // Компонент кнопки обновления
  const RefreshButton = () => (
    <button 
      className={`header-btn pc-refresh-btn ${refreshing ? 'pc-refreshing' : ''}`}
      onClick={handleRefresh}
      disabled={refreshing || loading}
    >
      <img 
        src="/st-icons/upload_icon.svg" 
        className={`pc-refresh-icon ${refreshing ? 'pc-refresh-spin' : ''}`}
        alt="Обновить"
      />
      <span>{refreshing ? 'Обновление...' : loading ? 'Загрузка...' : 'Обновить данные'}</span>
    </button>
  );

  // Фильтрация данных
  const filteredDisciplines = disciplines.filter(discipline => {
    const semester = discipline.idSubject % 2 === 0 ? 2 : 1;
    const semesterFilter = selectedSemester === 'first' ? semester === 1 : semester === 2;
    const courseFilter = selectedCourse === 'all' || discipline.course.toString() === selectedCourse;
    
    return semesterFilter && courseFilter;
  });

  // Сортировка по названию
  const sortedDisciplines = [...filteredDisciplines].sort((a, b) => {
    if (sortOrder === 'asc') {
      return a.subjectName.localeCompare(b.subjectName);
    } else {
      return b.subjectName.localeCompare(a.subjectName);
    }
  });

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleDisciplineClick = (disciplineName: string) => {
    if (onDisciplineSelect) {
      onDisciplineSelect(disciplineName);
    }
  };

  // Если данные пользователя еще не загружены
  if (!user) {
    return (
      <div className="ds-disciplines-section">
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Загрузка данных пользователя...
        </div>
      </div>
    );
  }

  return (
    <div className="ds-disciplines-section">
      {/* Заголовок с кнопками */}
      <div className="ds-cabinet-header">
        <InfoIcon />
        <RefreshButton />
      </div>

      {showCacheWarning && <CacheWarning />}

      {selectedDiscipline && (
        <div className="ds-discipline-filter">
          <div className="ds-discipline-info">
            <strong className="ds-discipline-name">{selectedDiscipline}</strong>
          </div>
          <button 
            className="ds-clear-discipline" 
            onClick={() => onDisciplineSelect && onDisciplineSelect(undefined)}
          >
            Сбросить фильтр
          </button>
        </div>
      )}

      {error && !isUsingCache && (
        <div className="ds-error-message">
          {error}
        </div>
      )}

      <div className="ds-disciplines-header">
        <div className="ds-view-tabs">
          <button
            className={`ds-view-tab ${activeTab === 'semesters' ? 'ds-active' : ''}`}
            onClick={() => setActiveTab('semesters')}
          >
            По семестрам
          </button>
          <button
            className={`ds-view-tab ${activeTab === 'course' ? 'ds-active' : ''}`}
            onClick={() => setActiveTab('course')}
          >
            По курсу
          </button>
        </div>
      </div>

      <div className="ds-disciplines-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Загрузка дисциплин...
          </div>
        ) : (
          <>
            {activeTab === 'semesters' ? (
              <>
                <div className="ds-semester-controls">
                  <div className="ds-semester-tabs">
                    <button
                      className={`ds-semester-tab ${selectedSemester === 'first' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedSemester('first')}
                    >
                      1-ый семестр
                    </button>
                    <button
                      className={`ds-semester-tab ${selectedSemester === 'second' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedSemester('second')}
                    >
                      2-ой семестр
                    </button>
                  </div>
                </div>

                <div className="ds-disciplines-table-container">
                  <table className="ds-disciplines-table">
                    <thead>
                      <tr>
                        <th className="ds-number-column">№</th>
                        <th className="ds-subject-column">
                          <div className="ds-column-header">
                            <span>Предмет</span>
                            <button 
                              className="ds-sort-button"
                              onClick={toggleSortOrder}
                            >
                              <img 
                                src="/th-icons/sort_icon.svg" 
                                alt="Сортировка" 
                                className={`ds-sort-icon ${sortOrder === 'desc' ? 'ds-sort-desc' : ''}`}
                              />
                            </button>
                          </div>
                        </th>
                        <th className="ds-course-column">
                          <div className="ds-header-text">Курс</div>
                        </th>
                        <th className="ds-groups-column">
                          <div className="ds-header-text">
                            Количество<br />групп
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDisciplines.length > 0 ? (
                        sortedDisciplines.map((discipline, index) => (
                          <tr 
                            key={discipline.idSubject} 
                            className={`ds-discipline-row ${selectedDiscipline === discipline.subjectName ? 'ds-selected' : ''}`}
                            onClick={() => handleDisciplineClick(discipline.subjectName)}
                          >
                            <td className="ds-number-cell">{index + 1}.</td>
                            <td className="ds-subject-name">{discipline.subjectName}</td>
                            <td className="ds-course-cell">{discipline.course}</td>
                            <td className="ds-groups-cell">{discipline.countGroup}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                            Второй семестр пока недоступен
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                <div className="ds-course-controls">
                  <div className="ds-course-tabs">
                    <button
                      className={`ds-course-tab ${selectedCourse === 'all' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedCourse('all')}
                    >
                      Все курсы
                    </button>
                    <button
                      className={`ds-course-tab ${selectedCourse === '1' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedCourse('1')}
                    >
                      1 курс
                    </button>
                    <button
                      className={`ds-course-tab ${selectedCourse === '2' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedCourse('2')}
                    >
                      2 курс
                    </button>
                    <button
                      className={`ds-course-tab ${selectedCourse === '3' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedCourse('3')}
                    >
                      3 курс
                    </button>
                    <button
                      className={`ds-course-tab ${selectedCourse === '4' ? 'ds-active' : ''}`}
                      onClick={() => setSelectedCourse('4')}
                    >
                      4 курс
                    </button>
                  </div>
                </div>

                <div className="ds-disciplines-table-container">
                  <table className="ds-disciplines-table">
                    <thead>
                      <tr>
                        <th className="ds-number-column">№</th>
                        <th className="ds-subject-column">
                          <div className="ds-column-header">
                            <span>Предмет</span>
                            <button 
                              className="ds-sort-button"
                              onClick={toggleSortOrder}
                            >
                              <img 
                                src="/th-icons/sort_icon.svg" 
                                alt="Сортировка" 
                                className={`ds-sort-icon ${sortOrder === 'desc' ? 'ds-sort-desc' : ''}`}
                              />
                            </button>
                          </div>
                        </th>
                        <th className="ds-course-column">
                          <div className="ds-header-text">Курс</div>
                        </th>
                        <th className="ds-groups-column">
                          <div className="ds-header-text">
                            Количество<br />групп
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDisciplines.length > 0 ? (
                        sortedDisciplines.map((discipline, index) => (
                          <tr 
                            key={discipline.idSubject} 
                            className={`ds-discipline-row ${selectedDiscipline === discipline.subjectName ? 'ds-selected' : ''}`}
                            onClick={() => handleDisciplineClick(discipline.subjectName)}
                          >
                            <td className="ds-number-cell">{index + 1}.</td>
                            <td className="ds-subject-name">{discipline.subjectName}</td>
                            <td className="ds-course-cell">{discipline.course}</td>
                            <td className="ds-groups-cell">{discipline.countGroup}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                            Нет дисциплин для отображения
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};